import './LocalPage.css';
import React from 'react';
import { Button, Alert, ProgressBar, Carousel, DropdownButton, Dropdown } from 'react-bootstrap';
import { connect } from 'react-redux';
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import * as sagas from '../../store/sagas';
import RTViewer from '../rtviewer/RTViewer';
import { StoreState } from '../../store/store';
import { ErrorBoundary } from '../ErrorBoundary';

import { DISPLAY_VERSION, NOT_FOR_CLINICAL_USE_LABELING, SECURITY_DISCLAIMER, getAppName } from '../../environments';
import GlobalToolbar from '../common/GlobalToolbar';
import { PseudonymizedTagsSummary, ServiceAgreementSummary } from '../common/ServiceAgreement';
import { sleep } from '../../util';
import LandingPageLayout from '../LandingPageLayout';
import _ from 'lodash';
import { getIsAutoOpenScanOnFileLoadEnabled, setIsAutoOpenScanOnFileLoadEnabled } from '../../local-storage';

type OwnProps = {}
type DispatchProps = {
    unloadAllScans(): void,
    unloadStructureSet(scanId: string, structureSetId: string): void,
    storeLocalScan(files: File[], cbReturnId: (scanId: string) => Promise<void>, allowMissingMetaheaderFix?: boolean): void,
    storeLocalStructureSet(file: File, currentScanId: string, cbReturnId: (scanId: string, ssId: string) => void): void,
    clearFileLoadErrors(): void,
}

type AllProps = OwnProps & StoreState & DispatchProps;
type OwnState = {
    showRTViewer: boolean,
    immediatelyOpenScan: boolean,
    scanId?: string | null,
    structureSetId?: string | null,
    slideIndex: number;
    selectedScanIfMultiple?: any,
}

const PREVENT_OPEN_IF_ERRORS = false;

type CarouselItem = { url: string, captionHeader?: string, captionText?: string };

const carouselItems: CarouselItem[] = [
    { url: '/img/carousel01.png', captionHeader: 'An image caption can go here', captionText: 'A description of the image can be put here, but it\'s optional' },
    { url: '/img/carousel02.png' }
];

const ERROR_MESSAGE_BUFFER = 'Request more than currently allocated buffer';

export class LocalPage extends React.Component<AllProps, OwnState> {
    displayName = LocalPage.name

    constructor(props: AllProps) {
        super(props);

        this.state = { showRTViewer: false, immediatelyOpenScan: true, slideIndex: 0 };
    }

    componentDidMount(): void {
        const isAutoOpenScanOnFileLoadEnabled = getIsAutoOpenScanOnFileLoadEnabled();
        if (isAutoOpenScanOnFileLoadEnabled !== undefined) {
            this.setState({ immediatelyOpenScan: isAutoOpenScanOnFileLoadEnabled });
        }
    }

    openScan = async (scanId: string, noWait: boolean = false) => {
        // sleep for a bit to let progress bars etc finish
        if (!noWait) {
            await sleep(500);
        }

        this.setState({ scanId: scanId });
        if (this.state.immediatelyOpenScan && (!this.props.fileLoadErrors || this.props.fileLoadErrors.length === 0) && Object.keys(this.props.scans).length === 1) {
            this.setState({ showRTViewer: true });
        }
    }

    openStructureSet = (scanId: string, structureSetId: string) => {
        if (this.state.scanId !== scanId) {
            alert("Structure set doesn't belong to current image");
        }
        else {
            this.setState({ structureSetId: structureSetId });
        }
    }

    handleImageFileChange = (e: any) => {
        this.props.clearFileLoadErrors();
        const allowMissingMetaheaderFix = this.props.deploymentConfigInfo && this.props.deploymentConfigInfo.allowMissingDicomMetaheaderFix;
        this.props.unloadAllScans();
        e.persist();
        this.setState({ scanId: null, structureSetId: null }, () => {
            let files = [];
            for (let i = 0; i < e.target.files.length; i++) {
                files.push(e.target.files[i])
            }
            if (files.length > 0) {
                this.props.storeLocalScan(files, this.openScan, allowMissingMetaheaderFix);
            }
        });
    }

    handleStructureSetFileChange = (e: any) => {
        if (!this.state.scanId) return;
        let scanId: string = this.state.scanId;

        if (this.state.structureSetId) {
            this.props.unloadStructureSet(scanId, this.state.structureSetId);
        }
        e.persist();
        this.setState({ structureSetId: null }, () => {
            this.props.storeLocalStructureSet(e.target.files[0], scanId, this.openStructureSet);
        })
    }


    handleShowRTViewerClick = () => {
        this.setState({ showRTViewer: true });
    }

    handleBack = () => {
        this.setState({ showRTViewer: false });
    }

    handleImmediatelyOpenChange = () => {
        setIsAutoOpenScanOnFileLoadEnabled(!this.state.immediatelyOpenScan)
        this.setState({ immediatelyOpenScan: !this.state.immediatelyOpenScan });
    }

    handleChangeSlide = (nextSlideIndex: number) => {
        this.setState({ slideIndex: nextSlideIndex });
    }

    getScanTitle = (scan: any): string => {
        const { seriesDescription, protocolName, studyDescription, bodyPartExamined } = scan;

        let scanName = '<Scan>';

        if (_.isString(seriesDescription) && seriesDescription.trim() !== '') { scanName = seriesDescription; }
        else if (_.isString(protocolName) && protocolName.trim() !== '') { scanName = protocolName; }
        else if (_.isString(studyDescription) && studyDescription.trim() !== '') { scanName = studyDescription; }
        else if (_.isString(bodyPartExamined) && bodyPartExamined.trim() !== '') { scanName = bodyPartExamined; }

        if (_.has(scan, 'modality')) {
            scanName = `${scanName} (${scan.modality})`;
        }

        return `${scanName}   `;
    }

    handleSelectScan = (scan: any) => {
        this.openScan(scan.scanId, true);
    }

    renderScanSelector = () => {
        const scans = this.props.scans ? Object.values(this.props.scans) : [];
        if (scans.length < 2) {
            return null;
        }
        else {
            const dropdownTitle = this.state.scanId ? this.getScanTitle(scans.find((s: any) => s.scanId === this.state.scanId)) : 'Select scan';
            return (
                <>
                    <div className="rtstruct-form info1"><p>3. Select scan to open:</p></div>
                    <DropdownButton id="scan-selector" title={dropdownTitle} variant="outline-info" size="sm">
                        {
                            scans.map((scan: any, index) =>
                                <Dropdown.Item as="button" key={scan.scanId} onClick={() => this.handleSelectScan(scan)}>
                                    Scan #{index + 1} -- {this.getScanTitle(scan)}
                                </Dropdown.Item>
                            )}
                    </DropdownButton>
                </>
            );
        }
    }

    renderFileForm = (isVisible: boolean) => {

        const { fileLoadErrors, localFileProgress, deploymentConfigInfo } = this.props;
        const isServiceAgreementVisible = this.props.applicationPermissions && this.props.applicationPermissions.showServiceAgreement;

        const isOpenPrevented = PREVENT_OPEN_IF_ERRORS && (fileLoadErrors !== undefined && fileLoadErrors.length > 0);

        // by hiding instead of not rendering the file form we're able to save the state of the browser's built-in
        // file load forms, allowing us to show the number and names of files loaded in the UI after user presses
        // back on the main rtviewer view
        const hiddenStyle = isVisible ? '' : 'hide-file-form';

        const currentSlide = carouselItems[this.state.slideIndex];

        const multipleAvailableScans = this.props.scans && Object.keys(this.props.scans).length > 1;
        const immediatelyOpenScanState = this.state.immediatelyOpenScan && !multipleAvailableScans;

        return (
            <LandingPageLayout
                className={hiddenStyle}
                toolbar={(
                    <div className="global-toolbar-for-local">
                        <GlobalToolbar isEmbedded={true} />
                    </div>
                )}
            >
                <div className="landing-page-container">
                    <div className='landing-left'>
                        <p className='info1'><b>{getAppName()}</b> can be used to auto-generate structure sets for input DICOM images.</p>

                        <p className='info1'>Images are pseudonymized before sending them to the delineation service.</p>

                        <div className="image-form info1">
                            <div className='rtstruct-form'>
                                <p >1. Select DICOM image files (CT/MR):</p>
                                <div className="dark-file-input-container">
                                    <label className="dark-file-input-label turquoise">
                                        Select files
                                    <input
                                            type="file"
                                            className="dark-file-input"
                                            onChange={this.handleImageFileChange}
                                            multiple
                                        />
                                    </label>
                                </div>
                            </div>
                            <div > <label><input name="OpenFileSelection" type="checkbox"
                                checked={immediatelyOpenScanState} onChange={this.handleImmediatelyOpenChange} disabled={multipleAvailableScans} /> Immediately open selected scan</label></div>

                            {localFileProgress && (<div className="scan-progress-bar"><ProgressBar variant="info" now={localFileProgress.progress} max={localFileProgress.total} /></div>)}

                        </div>
                        {this.state.scanId ? <div className="rtstruct-form info1"><p>2. Select DICOM structure set files (optional):</p>
                            <div className="dark-file-input-container">
                                <label className="dark-file-input-label turquoise" style={{
                                    width: "max-content"
                                }}>
                                    Add file<input type="file" ref="structureSetInput"
                                        className="dark-file-input" onChange={this.handleStructureSetFileChange} />
                                </label>
                            </div></div> : null
                        }
                        {this.state.scanId && !isOpenPrevented ?
                            <div className='rtviewer-btn'>
                                <span>{this.renderScanSelector()}</span>
                                <span><Button className="open-rtviewer-button btn btn-default col-card-btn turquoise" onClick={this.handleShowRTViewerClick}><img width="40" src="/img/logo-mvision-fav.svg" alt="MVision logo" />Open {getAppName()}</Button></span>
                            </div>
                            : null
                        }


                        {fileLoadErrors && fileLoadErrors.length > 0 && (
                            <Alert variant="warning" className="file-load-errors-alert">
                                <Alert.Heading>File load warning{fileLoadErrors.length !== 1 ? 's' : ''}</Alert.Heading>
                                <div>Following files could not be loaded and were ignored.</div>
                                {fileLoadErrors.map(e => (
                                    <div key={e.fileName} className="file-load-errors">
                                        <div className="filename"><b>{e.fileName}</b></div>
                                        <div className="error-message">{e.errorMessage === ERROR_MESSAGE_BUFFER ? 'File format is not supported.' : e.errorMessage}</div>
                                    </div>
                                ))}
                            </Alert>
                        )}

                        <p className='security-disclaimer'>{SECURITY_DISCLAIMER}</p>

                        {deploymentConfigInfo && deploymentConfigInfo.supportContactInformation && (
                            <div className="support-contact"><ReactMarkdown remarkPlugins={[remarkGfm]} children={deploymentConfigInfo.supportContactInformation} className="help-dialog-markdown" /></div>
                        )}

                        {isServiceAgreementVisible ? <ServiceAgreementSummary /> : <PseudonymizedTagsSummary />}

                        <div className="version-number">
                            <div>Version: {DISPLAY_VERSION}</div>
                            {deploymentConfigInfo && deploymentConfigInfo.regionInfo && (<div>Region: {deploymentConfigInfo.regionInfo}</div>)}
                            {deploymentConfigInfo && deploymentConfigInfo.termsOfUseDocumentUrl && (<div>By using this software you accept <a href={deploymentConfigInfo.termsOfUseDocumentUrl} target="_blank" rel="noopener noreferrer">the terms of use.</a></div>)}
                        </div>

                    </div>


                    <div className='landing-right'>
                        <img src={"/img/monitor-alena.png"} alt="logo" className="landing-image" />
                    </div>
                </div>
            </LandingPageLayout>
        );
    }

    render() {
        const scanId = this.state.scanId;
        const loginFailed = this.props.loginError !== null;

        if (loginFailed) { return null; }

        return (
            <>
                {this.renderFileForm(!(this.state.showRTViewer && scanId))}
                {this.state.showRTViewer && scanId &&
                    (<ErrorBoundary handleError={this.handleBack}>
                        <RTViewer scanId={scanId} canEdit={false} canCreateRtstruct={true} hideSaveButtons={true} handleBack={this.handleBack} />
                    </ErrorBoundary>)}
            </>
        );
    }
}

export default connect(
    state => Object.assign({}, state),
    sagas.mapDispatchToProps
)(LocalPage);
